/**
 * Invoices and payments — `mockups/studio/client-detail.html` §Invoices and payments.
 *
 * The mockup's own comment: "one row per invoice, because every invoice here has
 * exactly one payment against it for the same amount. Two tables of the same eight
 * numbers was the biggest restatement on the old page."
 *
 * So it is session 19's `InvoiceTable` in its **read-only** variant — the same three
 * rows above a fold and the same five under it, without the PDF column, which is what
 * `links={false}` is for. §DRY watchlist: "Table renderers for invoices, work orders,
 * tasks and payments are shared components, used by lists and by client detail."
 *
 * The mockup's answer line — "8 invoices, 8 payments, $24,904.86 since March 2026" —
 * loses its money: `StudioClientDetailSchema` carries the invoices and no total over
 * them, and adding eight `Money` values in a page is the arithmetic DESIGN.md P2
 * gives to one owner. The count and the first period are fields; the sum is in the
 * report as a missing one.
 *
 * **Read-only is the table, not the section.** Session 24 (M-2) found this table
 * rendering a `sent` invoice nobody had paid and a `processing` one with no control of
 * any kind, while `invoices_issue$`, `invoices_charge$`, `invoices_resend`,
 * `payments_record_manual$` and `payments_refund!` sat published and tested with no
 * caller anywhere in the SPA. `links={false}` still means "no PDF column" — that is a
 * client's own link — and the last column is now the studio's five moves,
 * `components/studio/InvoiceMoves.tsx`, one cell per row.
 *
 * **This is the surface that can offer Refund**, because it is the one holding
 * `InvoiceView.paid_amount`: a row with nothing settled against it has nothing to give
 * back, and the money page's compact ledger row cannot answer that question at all.
 *
 * **Income is listed under the invoices, and without them** (session 47). Far East
 * Novels' Patreon payouts settle no invoice, so the table above could never show them,
 * and an account with no invoices at all used to read "Nothing has been invoiced yet"
 * over money the studio had received for it. `IncomeList` prints the rows the read
 * model gives it and the rule that books them, and adds nothing up.
 */

import { Link } from '@tanstack/react-router'

import { isZero, type StudioClientDetailView, type StudioClientIncomeView } from '@fareast/shared'

import { InvoiceTable, railLabel } from '../../../components/client/index.js'
import { InvoiceMoves } from '../../../components/studio/InvoiceMoves.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { Region } from '../../../components/portal/Region.js'
import { Tag } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Answer, DateText, More, Muted, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'

export const MONEY_ID = 'invoices'

export type MoneySectionProps = {
  readonly detail: StudioClientDetailView
}

export function MoneySection({ detail }: MoneySectionProps) {
  const format = useFormat()
  const { invoices, income } = detail

  if (invoices.length === 0) {
    return (
      <Section id={MONEY_ID}>
        <SectionHead title="Invoices and payments" />
        <Empty>Nothing has been invoiced yet.</Empty>
        <IncomeList income={income} />
      </Section>
    )
  }

  /* The oldest row is the last: the read model answers most recent first. */
  const oldest = invoices[invoices.length - 1]
  const newest = invoices[0]
  const outstanding = invoices.filter((one) => one.status !== 'paid' && one.status !== 'draft')
  const rail = railLabel(newest?.rail_expected ?? null)

  return (
    <Section id={MONEY_ID}>
      <SectionHead title="Invoices and payments" />

      <Region>
        <Answer>
          {format.qty(invoices.length, { one: 'invoice', other: 'invoices' })} since{' '}
          {/*
            Read in **UTC**, like every other invoice date on this page and for the
            same reason `InvoiceTable` gives: an invoice's date is a day the studio
            names, not an instant the reader was present for.
          */}
          {oldest?.issued_at === null || oldest?.issued_at === undefined
            ? 'the account opened'
            : format.date(oldest.issued_at, 'short', 'UTC')}
          {outstanding.length === 0
            ? ' — nothing outstanding.'
            : ` — ${format.qty(outstanding.length, {
                one: 'invoice',
                other: 'invoices',
              })} still to settle.`}
        </Answer>
      </Region>

      <Card>
        <InvoiceTable
          invoices={invoices}
          links={false}
          actionsLabel="Do"
          actions={(invoice) => (
            <InvoiceMoves invoice={invoice} refundable={!isZero(invoice.paid_amount)} />
          )}
        />
        <CardBody>
          <Small>
            <Muted>
              {rail === null ? (
                'No rail has been chosen for this account yet.'
              ) : (
                <>
                  The last one was charged by <Tag>{rail}</Tag>.
                </>
              )}{' '}
            </Muted>
            {/* A router link, per CONTRACT.md §Internal links — not a page load. */}
            <More asChild>
              <Link to="/studio/money">The whole billing run →</Link>
            </More>
          </Small>
        </CardBody>
      </Card>
      <IncomeList income={income} />
    </Section>
  )
}

/**
 * Money booked to this account as income, with no invoice — and the rule that books it.
 *
 * Nothing at all for an account with neither, which is every account but one today: a
 * heading over an empty table would be a fact about nobody.
 */
function IncomeList({ income }: { readonly income: StudioClientIncomeView }) {
  const format = useFormat()
  const ruled = income.credits_are_income && income.sender_names.length > 0
  if (income.payments.length === 0 && !ruled) return null

  return (
    <Card>
      <CardBody>
        <Small>
          <Muted>
            {ruled
              ? `Income — Wise credits from ${income.sender_names.join(', ')} are booked here as they arrive, with no invoice.`
              : 'Income — booked here from the review queue, with no invoice.'}
          </Muted>
        </Small>
      </CardBody>
      {income.payments.length === 0 ? (
        <CardBody>
          <Empty>No income has been booked yet.</Empty>
        </CardBody>
      ) : (
        <CardBody scrollX tight>
          <Table>
            <THead>
              <Tr>
                <Th>Received</Th>
                <Th right>Amount</Th>
                <Th>From</Th>
                <Th>Booked</Th>
              </Tr>
            </THead>
            <TBody>
              {income.payments.map((row) => (
                <Tr key={row.external_id}>
                  <Td nowrap>
                    <DateText style="day" value={row.received_at} />
                  </Td>
                  <Td right num>
                    {format.money(row.amount)}
                  </Td>
                  <Td>
                    <Small>{row.sender === '' ? <Muted>—</Muted> : row.sender}</Small>
                  </Td>
                  <Td>
                    <Small>
                      <Muted>
                        {row.booked_by === 'rule' ? 'By its sender' : row.note || 'By hand'}
                      </Muted>
                    </Small>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </CardBody>
      )}
    </Card>
  )
}
